import React, { useMemo, useCallback, useEffect, useRef } from 'react'
import { TextField, InputAdornment, CircularProgress } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { makeStyles } from '@material-ui/core/styles'
import useOpenSearch from '../hooks/useOpenSearch'

// API configuration for debounce default
const API_CONFIG = {
  defaultDebounceMs: 600
}

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%'
  },
  listbox: {
    maxHeight: 250,
    overflow: 'auto'
  },
  noOptions: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary
  },
  loading: {
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  input: {
    padding: theme.spacing(1),
  },
  inputRoot: {
    padding: theme.spacing(1),
  },
}))

/**
 * OpenSearchDropdown - A reusable dropdown component with search capabilities
 * 
 * @param {Object} props
 * @param {string} props.id - Unique identifier for the dropdown
 * @param {string} props.page - Page identifier for search
 * @param {string} props.entity - Entity identifier for search
 * @param {string[]} props.searchFields - Fields to search in
 * @param {string} props.displayField - Field to display in dropdown (defaults to "DISPLAY")
 * @param {string} props.valueField - Field to use as value (defaults to "VALUE")
 * @param {string} props.label - Label for the input
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.required - Whether the input is required
 * @param {boolean} props.disabled - Whether the dropdown is disabled
 * @param {string} props.width - Width of the dropdown
 * @param {string} props.variant - TextField variant (standard, filled, outlined)
 * @param {string} props.accessToken - Access token for API calls
 * @param {Object} props.credentials - Credentials for API calls
 * @param {Object} props.filters - Additional filters for the search
 * @param {Function} props.onChange - Callback when value changes
 * @param {string} props.customError - Custom error message
 * @param {boolean} props.clearTrigger - Toggle this to clear the dropdown
 */
function OpenSearchDropdown({
  id,
  page,
  entity,
  searchFields = ['DISPLAY'],
  label = 'Search',
  placeholder = 'Start typing to search...',
  required = false,
  disabled = false,
  width = '100%',
  variant = 'standard',
  debounceMs = API_CONFIG.defaultDebounceMs,
  accessToken,
  credentials,
  displayField = 'DISPLAY',
  valueField = 'VALUE',
  filters = {},
  onChange,
  customError = null,
  clearTrigger = false,
  initialValue = null
}) {
  const classes = useStyles()
  const prevClearTriggerRef = useRef(clearTrigger)
  const inputRef = useRef(null)
  
  const {
    loading,
    options,
    inputValue,
    selectedValue,
    touched,
    isIndexNotFound,
    handleInputChange,
    handleChange,
    handleOpen,
    handleBlur,
    resetTouched,
    setValue,
    setInputValueDirect,
    searchItems
  } = useOpenSearch({
    page,
    entity,
    searchFields,
    accessToken,
    credentials,
    debounceMs,
    filters
    // Don't pass clearTrigger to hook - handle it in component instead
  })

  // Helper function to check if inputValue is blank
  const isInputBlank = (value) => {
    return value == null || value.trim() === ''
  }

  // Sort options alphabetically by displayField only when input is blank
  const sortedOptions = useMemo(() => {
    if (isInputBlank(inputValue)) {
      return [...options].sort((a, b) => {
        const aValue = a?.[displayField] || ''
        const bValue = b?.[displayField] || ''
        return aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: 'base' })
      })
    }
    return options
  }, [options, inputValue, displayField])

  const getError = useCallback(() => {
    if (!touched) return null
    if (customError) return customError
    if (required && !selectedValue) return `${label} must be selected`
    return null
  }, [touched, customError, required, selectedValue, label])

  // Handle clearTrigger changes - call handleChange to ensure onChange callback is triggered
  // This makes the Clear button behave the same way as clicking the X mark
  useEffect(() => {
    // Detect any change in clearTrigger (it's toggled, so any change means clear was clicked)
    if (clearTrigger !== prevClearTriggerRef.current) {
      // Use setValue instead of handleChange to avoid setting touched to true
      setValue(null)
      // Call onChange callback manually to notify parent
      if (onChange) {
        onChange(null)
      }
      // Clear the input value to match X mark behavior
      handleInputChange('')
      // Reset touched state to prevent error from showing when clearing via Clear button
      resetTouched()
      // Trigger search to refresh options (same as handleChange does when clearing)
      searchItems('')
    }
    prevClearTriggerRef.current = clearTrigger
  }, [clearTrigger, setValue, handleInputChange, onChange, resetTouched, searchItems])

  // Handle initialValue - set the dropdown value without triggering search
  const initialValueProcessed = useRef(false)
  useEffect(() => {
    if (initialValue && !initialValueProcessed.current) {
      initialValueProcessed.current = true
      setValue(initialValue)
      setInputValueDirect(initialValue[displayField] || '')
    }
  }, [initialValue, setValue, setInputValueDirect, displayField])

  // Mark field as touched when form is submitted (for submit-time validation)
  useEffect(() => {
    const inputElement = inputRef.current
    if (!inputElement) return

    // Find the form element
    const form = inputElement.closest('form')
    if (!form) return

    const handleFormSubmit = (e) => {
      // If field is required and empty, mark as touched to show error
      if (required && !selectedValue) {
        // Use handleBlur to mark as touched (it will check if value is empty)
        handleBlur()
      }
    }

    form.addEventListener('submit', handleFormSubmit)
    return () => {
      form.removeEventListener('submit', handleFormSubmit)
    }
  }, [required, selectedValue, handleBlur])

  // Memoize callbacks at the top level (not inside JSX)
  const handleAutocompleteChange = useCallback((event, newValue) => {
    handleChange(newValue, onChange)
  }, [handleChange, onChange])

  const handleAutocompleteInputChange = useCallback((event, newInputValue) => {
    handleInputChange(newInputValue)
  }, [handleInputChange])

  const getOptionLabel = useCallback((option) => {
    return option?.[displayField] || ''
  }, [displayField])

  const getOptionSelected = useCallback((option, value) => {
    return option?.[valueField] === value?.[valueField]
  }, [valueField])

  const renderOption = useCallback((option) => {
    return (
      <div>
        {option[displayField]}
      </div>
    )
  }, [displayField])

  return (
    <Autocomplete
      id={id}
      className={classes.root}
      style={{ width }}
      value={selectedValue}
      onChange={handleAutocompleteChange}
      inputValue={inputValue}
      onInputChange={handleAutocompleteInputChange}
      options={sortedOptions}
      getOptionLabel={getOptionLabel}
      getOptionSelected={getOptionSelected}
      loading={loading}
      disabled={disabled}
      onOpen={handleOpen}
      openOnFocus
      filterOptions={(x) => x}
      noOptionsText={isIndexNotFound ? 'No Data' : 'No options'}
      classes={{
        listbox: classes.listbox,
        noOptions: classes.noOptions,
        loading: classes.loading,
        ...(variant !== 'filled' && {
          input: classes.input,
          inputRoot: classes.inputRoot
        })
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={(ref) => {
            inputRef.current = ref
            if (params.inputRef) {
              params.inputRef(ref)
            }
          }}
          label={label}
          placeholder={placeholder}
          required={required}
          error={!!getError()}
          helperText={getError()}
          variant={variant}
          onBlur={handleBlur}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
      renderOption={renderOption}
    />
  )
}

export default OpenSearchDropdown
